import { api, handleApiError } from '../client'

export interface Team {
  id: string
  name: string
  description?: string
  color?: string
  memberCount: number
  isActive: boolean
  createdAt: string
}

export interface TeamMember {
  id: string
  userId: string
  userEmail: string
  userFullName: string
  role: string
  joinedAt: string
}

export interface TeamDetail extends Team {
  members: TeamMember[]
}

export interface CreateTeamRequest {
  name: string
  description?: string
  color?: string
}

export interface UpdateTeamRequest {
  name: string
  description?: string
  color?: string
}

export interface AddTeamMemberRequest {
  userId: string
  role?: string
}

export const teamService = {
  async getTeams(tenantId?: string): Promise<Team[]> {
    try {
      const params = tenantId ? { tenantId } : undefined
      const response = await api.get<Team[]>('/v1/teams', { params })
      return response.data
    } catch (error) {
      throw new Error(handleApiError(error))
    }
  },

  async getTeam(id: string): Promise<TeamDetail> {
    try {
      const response = await api.get<TeamDetail>(`/v1/teams/${id}`)
      return response.data
    } catch (error) {
      throw new Error(handleApiError(error))
    }
  },

  async createTeam(data: CreateTeamRequest): Promise<Team> {
    try {
      const response = await api.post<Team>('/v1/teams', data)
      return response.data
    } catch (error) {
      throw new Error(handleApiError(error))
    }
  },

  async updateTeam(id: string, data: UpdateTeamRequest): Promise<Team> {
    try {
      const response = await api.put<Team>(`/v1/teams/${id}`, data)
      return response.data
    } catch (error) {
      throw new Error(handleApiError(error))
    }
  },

  async deleteTeam(id: string): Promise<void> {
    try {
      await api.delete(`/v1/teams/${id}`)
    } catch (error) {
      throw new Error(handleApiError(error))
    }
  },

  async addMember(teamId: string, data: AddTeamMemberRequest): Promise<TeamMember> {
    try {
      const response = await api.post<TeamMember>(`/v1/teams/${teamId}/members`, data)
      return response.data
    } catch (error) {
      throw new Error(handleApiError(error))
    }
  },

  async removeMember(teamId: string, memberId: string): Promise<void> {
    try {
      await api.delete(`/v1/teams/${teamId}/members/${memberId}`)
    } catch (error) {
      throw new Error(handleApiError(error))
    }
  },

  async updateMemberRole(teamId: string, memberId: string, role: string): Promise<TeamMember> {
    try {
      const response = await api.put<TeamMember>(`/v1/teams/${teamId}/members/${memberId}?role=${role}`)
      return response.data
    } catch (error) {
      throw new Error(handleApiError(error))
    }
  },
}
